import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_TITLE = 'GRAWEO - Charitable Foundation Supporting Ukraine';
const BASE_DESCRIPTION =
  'GRAWEO is a charitable foundation providing humanitarian aid, support for internally displaced persons, and small business assistance.';
const BASE_URL = 'https://graweo.org';

function setMetaByName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function setMetaByProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

export function useSEO({ title, description, canonical, noindex = false, video } = {}) {
  const location = useLocation();

  const videoName = video?.name;
  const videoDescription = video?.description;
  const videoEmbedUrl = video?.embedUrl;
  const videoContentUrl = video?.contentUrl;
  const videoThumbnailUrl = video?.thumbnailUrl;
  const videoUploadDate = video?.uploadDate;

  useEffect(() => {
    const fullTitle = title ? `${title} | GRAWEO` : BASE_TITLE;
    const desc = description || BASE_DESCRIPTION;
    const url = canonical || `${BASE_URL}${location.pathname}`;

    document.title = fullTitle;

    setMetaByName('description', desc);
    setMetaByName('robots', noindex ? 'noindex, follow' : 'index, follow');

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', url);

    setMetaByProperty('og:title', fullTitle);
    setMetaByProperty('og:description', desc);
    setMetaByProperty('og:url', url);

    setMetaByName('twitter:title', fullTitle);
    setMetaByName('twitter:description', desc);

    const VIDEO_SCHEMA_ID = 'schema-videoobject';
    let videoScript = document.getElementById(VIDEO_SCHEMA_ID);
    if (videoName && videoEmbedUrl) {
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: videoName,
        description: videoDescription,
        thumbnailUrl: videoThumbnailUrl,
        embedUrl: videoEmbedUrl,
        contentUrl: videoContentUrl,
        uploadDate: videoUploadDate,
        publisher: {
          '@type': 'Organization',
          name: 'GRAWEO Charitable Foundation',
          url: 'https://graweo.org',
        },
      };
      if (!videoScript) {
        videoScript = document.createElement('script');
        videoScript.setAttribute('type', 'application/ld+json');
        videoScript.setAttribute('id', VIDEO_SCHEMA_ID);
        document.head.appendChild(videoScript);
      }
      videoScript.textContent = JSON.stringify(schema);
    } else if (videoScript) {
      videoScript.remove();
    }

    return () => {
      document.title = BASE_TITLE;
      setMetaByName('description', BASE_DESCRIPTION);
      setMetaByName('robots', 'index, follow');
      setMetaByProperty('og:title', 'GRAWEO - Supporting Ukraine Charitable Foundation');
      setMetaByProperty('og:description', BASE_DESCRIPTION);
      setMetaByProperty('og:url', BASE_URL);
      setMetaByName('twitter:title', 'GRAWEO - Supporting Ukraine');
      setMetaByName('twitter:description', BASE_DESCRIPTION);
      const vs = document.getElementById(VIDEO_SCHEMA_ID);
      if (vs) vs.remove();
    };
  }, [title, description, canonical, noindex, location.pathname,
      videoName, videoDescription, videoEmbedUrl, videoContentUrl,
      videoThumbnailUrl, videoUploadDate]);
}
